/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { PartialBounds, PartialTerms } from '@artifact-terms';
import api from './api';

/**
 * **Standard Terms** — the seeded base tier every source's terms resolve over (`ST6`, `3c-ii`,
 * `#1398`). The console reads its terms for a source not yet saved, and edits them on the
 * Configuration page through the shared Terms box.
 *
 * Only what the console uses of the stored `PartnerDto`: its terms and, since `5c-i`, its restrictions.
 * The rest of a partner is `#837`'s. Typed by the shared `@artifact-terms`, not restated.
 */
export interface StandardTerms {
  id: string;
  name: string;
  terms?: PartialTerms;
  restrictions?: PartialBounds;
}

/** What a save states: the terms and the restrictions, each as the whole statement. */
export interface StandardTermsStatement {
  terms: PartialTerms;
  restrictions: PartialBounds;
}

export const standardTermsService = {
  async getStandardTerms(): Promise<StandardTerms> {
    const { data } = await api.get<StandardTerms>('/api/standard-terms');
    return data;
  },

  /** Saves both as the whole statement: a member left out is no longer stated. */
  async saveStandardTerms(statement: StandardTermsStatement): Promise<StandardTerms> {
    const { data } = await api.put<StandardTerms>('/api/standard-terms', statement);
    return data;
  },
};
